import React, { useState } from 'react';
import axios from 'axios';

const MinibusBooking: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    bookingStartDate: '',
    bookingEndDate: '',
    pickupLocation: '',
    dropoffLocation: '',
    numberOfPassengers: '',
    purposeOfHire: '',
    requiresDriver: false,
    insuranceAccepted: false
  });

  const [licenseFrontPhoto, setLicenseFrontPhoto] = useState<File | null>(null);
  const [licenseBackPhoto, setLicenseBackPhoto] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [pricing, setPricing] = useState<{
    numberOfDays: number;
    baseCost: number;
    driverCost: number;
    deposit: number;
    totalCost: number;
  } | null>(null);

  // Calculate pricing when dates or driver option changes
  const calculatePricing = (startDate: string, endDate: string, requiresDriver: boolean) => {
    if (!startDate || !endDate) {
      setPricing(null);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (end < start) {
      setPricing(null);
      return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const numberOfDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // Include both start and end day

    const baseCost = numberOfDays * 150; // £150 per day
    const driverCost = requiresDriver ? (numberOfDays * 150) : 0; // £150 per day if driver required
    const deposit = 300; // £300 deposit
    const totalCost = baseCost + driverCost + deposit;

    setPricing({ numberOfDays, baseCost, driverCost, deposit, totalCost });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Recalculate pricing if date or driver changes
    if (name === 'bookingStartDate') {
      calculatePricing(value, formData.bookingEndDate, formData.requiresDriver);
    } else if (name === 'bookingEndDate') {
      calculatePricing(formData.bookingStartDate, value, formData.requiresDriver);
    } else if (name === 'requiresDriver') {
      calculatePricing(formData.bookingStartDate, formData.bookingEndDate, checked);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'front' | 'back') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'front') {
        setLicenseFrontPhoto(file);
      } else {
        setLicenseBackPhoto(file);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    // Validation
    if (!licenseFrontPhoto || !licenseBackPhoto) {
      setMessage({ type: 'error', text: 'Please upload both license photos (front and back)' });
      setLoading(false);
      return;
    }

    if (!formData.insuranceAccepted) {
      setMessage({ type: 'error', text: 'You must accept the insurance terms to proceed' });
      setLoading(false);
      return;
    }

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, (formData as any)[key].toString());
      });

      // Add license photos
      submitData.append('licenseFrontPhoto', licenseFrontPhoto);
      submitData.append('licenseBackPhoto', licenseBackPhoto);

      const response = await axios.post('http://localhost:5000/api/minibus', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setMessage({ 
        type: 'success', 
        text: response.data.message || 'Booking submitted successfully! We will verify your license and contact you within 24 hours.' 
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        licenseNumber: '',
        licenseExpiry: '',
        bookingStartDate: '',
        bookingEndDate: '',
        pickupLocation: '',
        dropoffLocation: '',
        numberOfPassengers: '',
        purposeOfHire: '',
        requiresDriver: false,
        insuranceAccepted: false
      });
      setLicenseFrontPhoto(null);
      setLicenseBackPhoto(null);
      setPricing(null);

      // Scroll to top to see success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error: any) {
      console.error('Error submitting booking:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit booking. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  // Get today's date for min date validation
  const today = new Date();
  today.setDate(today.getDate() + 2); // Minimum 48 hours notice
  const minDate = today.toISOString().split('T')[0];

  // Maximum 6 months from today
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 6);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-800 mb-4">🚌 Book Our 17-Seater Minibus</h1>
          <p className="text-lg text-gray-700">
            Perfect for group outings, sports events, or community trips!
          </p>
          <div className="mt-4 inline-flex items-center bg-blue-100 border border-blue-300 rounded-lg px-4 py-2">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-blue-800">D1 License Required | Insurance Provided | Book 48h in advance</span>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 border border-green-300 text-green-800' : 'bg-red-100 border border-red-300 text-red-800'}`}>
            <div className="flex items-start">
              {message.type === 'success' ? (
                <svg className="w-6 h-6 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-6 h-6 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <p className="text-sm">{message.text}</p>
            </div>
          </div>
        )}

        {/* Booking Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
          
          {/* Personal Information */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="07123 456789"
                />
              </div>
            </div>
          </div>

          {/* D1 License Information */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">D1 License Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number *</label>
                <input
                  type="text"
                  name="licenseNumber"
                  value={formData.licenseNumber}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="SMIT123456AB9CD"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Expiry Date *</label>
                <input
                  type="date"
                  name="licenseExpiry"
                  value={formData.licenseExpiry}
                  onChange={handleInputChange}
                  required
                  min={minDate}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Photo (Front) *</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'front')}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {licenseFrontPhoto && (
                  <p className="mt-1 text-xs text-green-600">✓ {licenseFrontPhoto.name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Photo (Back) *</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileChange(e, 'back')}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                {licenseBackPhoto && (
                  <p className="mt-1 text-xs text-green-600">✓ {licenseBackPhoto.name}</p>
                )}
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              📸 Please upload clear photos of both sides of your D1 driving license
            </p>
          </div>

          {/* Booking Details */}
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-2">Booking Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  name="bookingStartDate"
                  value={formData.bookingStartDate}
                  onChange={handleInputChange}
                  required
                  min={minDate}
                  max={maxDateStr}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">Minimum 48 hours notice required</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  name="bookingEndDate"
                  value={formData.bookingEndDate}
                  onChange={handleInputChange}
                  required
                  min={formData.bookingStartDate || minDate}
                  max={maxDateStr}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">Maximum 6 months in advance</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location *</label>
                <input
                  type="text"
                  name="pickupLocation"
                  value={formData.pickupLocation}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Leftley Sports Club"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Location *</label>
                <input
                  type="text"
                  name="dropoffLocation"
                  value={formData.dropoffLocation}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Same as pickup or different location"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Passengers *</label>
                <input
                  type="number"
                  name="numberOfPassengers"
                  value={formData.numberOfPassengers}
                  onChange={handleInputChange}
                  required
                  min="1"
                  max="17"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="1-17"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose of Hire *</label>
                <input
                  type="text"
                  name="purposeOfHire"
                  value={formData.purposeOfHire}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Football match, Community trip"
                />
              </div>
            </div>
          </div>

          {/* Driver Option */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="requiresDriver"
                checked={formData.requiresDriver}
                onChange={handleInputChange}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label className="ml-3 text-sm font-medium text-gray-800">
                I require a driver (Additional £150/day)
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-600 ml-8">
              ℹ️ If you don't have a D1 license or prefer not to drive, we can provide an experienced driver
            </p>
          </div>

          {/* Pricing Summary */}
          {pricing && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">💰 Pricing Summary</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Number of Days:</span>
                  <span className="font-semibold">{pricing.numberOfDays} {pricing.numberOfDays === 1 ? 'day' : 'days'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Base Cost ({pricing.numberOfDays} × £150):</span>
                  <span className="font-semibold">£{pricing.baseCost}</span>
                </div>
                {pricing.driverCost > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Driver Cost ({pricing.numberOfDays} × £150):</span>
                    <span className="font-semibold">£{pricing.driverCost}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Security Deposit:</span>
                  <span className="font-semibold">£{pricing.deposit}</span>
                </div>
                <div className="border-t border-green-300 pt-2 mt-2"></div>
                <div className="flex justify-between text-lg font-bold text-green-800">
                  <span>Total Cost:</span>
                  <span>£{pricing.totalCost}</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  * Deposit is refundable if minibus is returned without damage
                </p>
              </div>
            </div>
          )}

          {/* Insurance Terms */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                name="insuranceAccepted"
                checked={formData.insuranceAccepted}
                onChange={handleInputChange}
                required
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
              />
              <div className="ml-3">
                <label className="text-sm font-medium text-gray-800">
                  I accept the insurance terms and conditions *
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  The minibus is fully insured for community use. The £300 deposit covers any damages or excessive cleaning required. You are responsible for the vehicle during your hire period.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-3 rounded-lg font-semibold text-white transition-all ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                '🚌 Submit Booking Request'
              )}
            </button>
          </div>

          <p className="text-center text-xs text-gray-500 mt-4">
            By submitting this form, you agree to our terms and conditions. We will verify your D1 license and contact you within 24 hours to confirm availability and payment details.
          </p>
        </form>
      </div>
    </div>
  );
};

export default MinibusBooking;
